// ---------------------------------------------------------------------------
// Onboarding Invites — ONB-002 Phase 2 (Tranche A schema)
// ---------------------------------------------------------------------------
// Story ONB-002: "Persona path must classify business owner / agency /
// team member / client and respect target completion times." Registry
// blocker per `ONB-002-DECISION-PACKET.md` (Tranche A — invite/magic-link
// entry, recommended next): non-owner personas (team_member + client) need
// an invite token that pre-binds a persona to an invitation so the SPA can
// skip the persona-pick step and land the invitee directly on the persona's
// first runtime surface.
//
// Phase 1 (already shipped on `origin/main`): mounted persona route +
// persistence + frontend regression coverage. Phase 2 (this file): the
// persistence layer the future invite mint/verify routes write to.
//
// Phase 3+ (still owed per ONB-002-DECISION-PACKET.md Tranche A):
//   - `workers/api-crm/src/routes/onboarding-invite.ts` (new) — POST mint
//     + GET verify routes
//   - `apps/ease-experience/src/routes/onboarding.tsx` — read `?invite=`
//     query param + skip persona-pick step when token verifies
//   - CQRS event `onboarding.invite_minted@v1` + `onboarding.invite_consumed@v1`
//
// Design rules locked here:
//   - One row per token; `token_hash` is the unique key for verification
//     (the raw token never persists — hashed at mint time so a DB leak
//     cannot recover the invite link).
//   - Persona binding is REQUIRED — every invite pre-selects exactly one
//     persona from the four-persona catalog (`owner` | `agency` |
//     `team_member` | `client`). CHECK constraint inlines the literals so
//     the DB fails closed on persona drift.
//   - `tenant_id` scopes the invite to the inviting tenant — cross-tenant
//     consumption is impossible because the verify route filters on
//     `tenant_id` derived from the token row itself.
//   - `expires_at` is required and must be after `created_at`; verify
//     routes still enforce the current-time expiry decision.
//   - `consumed_at` + `consumed_by_user_id` capture the one-shot
//     consumption semantics; idempotent re-verify returns the same persona
//     binding without re-consuming.
//   - `revoked_at` lets the inviter cancel a pending invite without
//     deleting the row (audit trail).
// ---------------------------------------------------------------------------

import {
  check,
  index,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

const tz = { withTimezone: true } as const;

/**
 * Closed persona catalog mirroring `ONBOARDING_PATHS` in
 * `apps/ease-experience/src/routes/onboarding.tsx`. The CHECK constraint
 * below inlines these literals so a future caller cannot write an
 * unsupported persona via raw SQL. Append-only — additions are fine but
 * removals require a registry update + decision-packet revision.
 */
export const ONBOARDING_INVITE_PERSONAS = [
  "owner",
  "agency",
  "team_member",
  "client",
] as const;

export type OnboardingInvitePersona = (typeof ONBOARDING_INVITE_PERSONAS)[number];

export function isOnboardingInvitePersona(
  value: unknown,
): value is OnboardingInvitePersona {
  return (
    typeof value === "string" &&
    (ONBOARDING_INVITE_PERSONAS as readonly string[]).includes(value)
  );
}

export const onboardingInvites = pgTable(
  "onboarding_invites",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    /**
     * Inviting tenant. Verify route uses this column to bootstrap the
     * invitee into the correct tenant context — cross-tenant consumption
     * is structurally impossible because the verify route reads tenant
     * from the row, not from request headers.
     */
    tenantId: uuid("tenant_id").notNull(),
    /**
     * SHA-256 hash of the raw token. The raw token is delivered to the
     * invitee out-of-band (magic-link email) and never persisted. The
     * verify route hashes the inbound `?invite=` param and looks up by
     * this column.
     */
    tokenHash: text("token_hash").notNull(),
    /**
     * Persona this invite pre-selects. The SPA skips the persona-pick
     * step on a valid invite and routes straight to this persona's first
     * runtime surface.
     */
    persona: text("persona").notNull(),
    /**
     * User who minted the invite (the inviting owner / agency). Captured
     * for audit + so the mint surface can re-show the inviter their
     * pending invites.
     */
    inviterUserId: text("inviter_user_id").notNull(),
    /**
     * Optional invitee email — set when the mint route knows the target
     * address (magic-link send). NULL for share-link mints where the
     * inviter copies the link to send themselves.
     */
    invitedEmail: text("invited_email"),
    /**
     * Hard expiry. The verify route MUST refuse a token where
     * `now() >= expires_at`, regardless of `consumed_at` / `revoked_at`.
     */
    expiresAt: timestamp("expires_at", tz).notNull(),
    /**
     * First consumption timestamp. NULL until the invitee verifies. Once
     * set, the row is consumed; idempotent re-verify by the same
     * `consumed_by_user_id` returns the same persona binding without
     * mutating the row (replay-safety for SPA refreshes).
     */
    consumedAt: timestamp("consumed_at", tz),
    /**
     * The user who consumed the invite. Populated on first verify. The
     * mint surface displays this so the inviter can confirm the right
     * person redeemed the link.
     */
    consumedByUserId: text("consumed_by_user_id"),
    /**
     * Revocation timestamp — the inviter cancels a pending invite. NULL
     * for active invites. The verify route MUST refuse a revoked token
     * even before `expires_at`.
     */
    revokedAt: timestamp("revoked_at", tz),
    /**
     * User who revoked the invite. NULL for active invites; set to the
     * inviter (or an admin) on revoke.
     */
    revokedByUserId: text("revoked_by_user_id"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    /**
     * Identity: token hashes MUST be globally unique. The verify route
     * keys on this column and a duplicate would let two distinct invites
     * map to the same token, breaking the one-shot consumption invariant.
     */
    uniqueIndex("onboarding_invites_token_hash_uq").on(table.tokenHash),
    /**
     * Per-tenant list scan: the mint surface lists active invites for the
     * inviting tenant. Small cardinality so a tenant-prefix index is
     * sufficient.
     */
    index("onboarding_invites_tenant_idx").on(table.tenantId),
    /**
     * Partial index over pending invites (not consumed, not revoked),
     * ordered by expiry. Keeps the storage proportional to the actionable
     * subset while the list/verify routes apply the current-time expiry
     * predicate.
     */
    index("onboarding_invites_active_idx")
      .on(table.tenantId, table.expiresAt)
      .where(sql`${table.consumedAt} IS NULL AND ${table.revokedAt} IS NULL`),
    /**
     * Closed-persona CHECK — fails closed at the DB layer if a future
     * caller writes a persona outside the four-persona catalog. Mirrors
     * the literal order in `ONBOARDING_INVITE_PERSONAS` so the verify
     * route + SPA branching stay aligned.
     */
    check(
      "onboarding_invites_persona_ck",
      sql`${table.persona} IN (${sql.join(
        ONBOARDING_INVITE_PERSONAS.map((p) => sql`${p}`),
        sql`, `,
      )})`,
    ),
    /**
     * Expiry integrity: every invite must have a forward validity window.
     * Route-time checks still compare `expires_at` against the current
     * clock, but the row itself cannot be written already expired relative
     * to its creation timestamp.
     */
    check(
      "onboarding_invites_expiry_after_created_ck",
      sql`${table.expiresAt} > ${table.createdAt}`,
    ),
    /**
     * Consumption integrity: `consumed_at` and `consumed_by_user_id`
     * MUST be set together (both NULL = active, both set = consumed).
     * Prevents partial-write states where one column is set without the
     * other (e.g. a buggy verify route that writes the timestamp without
     * the user id, leaving the row in a non-replayable state).
     */
    check(
      "onboarding_invites_consumption_consistency_ck",
      sql`(${table.consumedAt} IS NULL AND ${table.consumedByUserId} IS NULL)
          OR (${table.consumedAt} IS NOT NULL AND ${table.consumedByUserId} IS NOT NULL)`,
    ),
    /**
     * Revocation integrity mirror of the consumption rule — `revoked_at`
     * + `revoked_by_user_id` are both NULL or both set.
     */
    check(
      "onboarding_invites_revocation_consistency_ck",
      sql`(${table.revokedAt} IS NULL AND ${table.revokedByUserId} IS NULL)
          OR (${table.revokedAt} IS NOT NULL AND ${table.revokedByUserId} IS NOT NULL)`,
    ),
    /**
     * Terminal-state integrity: a consumed invite cannot later be revoked,
     * and a revoked invite cannot later be consumed. The historical row is
     * one terminal fact, not two conflicting facts.
     */
    check(
      "onboarding_invites_single_terminal_state_ck",
      sql`${table.consumedAt} IS NULL OR ${table.revokedAt} IS NULL`,
    ),
  ],
);

export type OnboardingInvite = typeof onboardingInvites.$inferSelect;
export type NewOnboardingInvite = typeof onboardingInvites.$inferInsert;
